import React from 'react'
import { Box } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import EventIcon from '@mui/icons-material/Event'
import { __t } from '../../../../../i18n/t'
import { dowShort, todayStr } from '../../format'
import {
  Audience, Hours, RangeId, When, dayFull, hasTimeInputs, isOvernight, perDayLines, perDayOn, pickPreset, presetLabel, singleWindow, whenSentence,
} from '../../model'
import { useWiz } from '../../state/WizardContext'
import { Callout, FieldError, Hint } from '../../ui/basics'
import { CheckRow, LabeledInput, OutlineButton, Pill, RadioCard, RadioRow, TextButton } from '../../ui/controls'
import { C, CALLOUT, focusRing, stackStartSx } from '../../ui/tokens'

const RANGES = [RangeId.today, RangeId.weekend, RangeId.week, RangeId.month]

// Estilo fora do JSX: o plugin de i18n embrulha literal de texto dentro de `&&`/ternário (ver ui/tokens.ts).
// A etapa já pergunta "Para quem/Quando vale a promoção?": sem subtítulo que repita a pergunta.
const stepSx = { display: 'flex', flexDirection: 'column', gap: 1.25 } as const
const panelSx = { display: 'flex', flexDirection: 'column', gap: 2.25, p: 2.25, border: `1px solid ${C.line}`, borderRadius: '12px', bgcolor: C.surface, maxWidth: 760 } as const
const blockSx = { display: 'flex', flexDirection: 'column', gap: 1.25 } as const
const titleSx = { fontSize: 14.5, fontWeight: 500, color: C.ink } as const
const pillsSx = { display: 'flex', flexWrap: 'wrap', gap: 0.75 } as const
const datesSx = { display: 'flex', flexWrap: 'wrap', gap: 1.5 } as const
const hoursGroupSx = { display: 'flex', flexWrap: 'wrap', gap: 1, '& > label': { flex: '1 1 190px', maxWidth: { sm: 260 } } } as const
const cardTightSx = { p: '12px 14px' } as const
const timesSx = { display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 1.5 } as const
const sentenceBoxSx = {
  display: 'flex', gap: 1, alignItems: 'flex-start', p: '10px 12px', borderRadius: '8px', bgcolor: '#fff', border: `1px solid ${C.line}`, fontSize: 14, color: C.ink, lineHeight: 1.45,
} as const
const weekdayGroupSx = { display: 'flex', flexWrap: 'wrap', gap: 0.75, position: 'relative' } as const
const perDayListSx = { listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 0.5, fontSize: 14, color: C.ink } as const
const perDayRowSx = { display: 'flex', flexWrap: 'wrap', gap: '0 12px', py: 0.5, borderBottom: `1px solid ${C.lineSoft}` } as const
const perDayDaySx = { minWidth: 110, fontWeight: 700, textTransform: 'capitalize' } as const
const warnTextSx = { maxWidth: 760 } as const
const eventIconSx = { fontSize: 18, color: C.violet, mt: '1px', flexShrink: 0 } as const
const downPadSx = { pb: 1.25 } as const

const WeekdayPill: React.FC<{ index: number; on: boolean; toggle: () => void }> = ({ index, on, toggle }) => {
  const short = dowShort(index)
  return (
    <Box
      component="label"
      sx={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 54, height: { xs: 44, sm: 40 }, px: 1.25, borderRadius: 999, cursor: 'pointer',
        border: `1.5px solid ${on ? C.violet : C.inputBorder}`, bgcolor: on ? C.violet : '#fff', color: on ? '#fff' : C.ink, fontSize: 14, fontWeight: on ? 700 : 500,
        textTransform: 'capitalize', '&:has(input:focus-visible)': focusRing,
      }}
    >
      <input
        type="checkbox"
        checked={on}
        onChange={toggle}
        aria-label={dayFull(index)}
        style={visuallyHidden as React.CSSProperties}
      />
      {short}
    </Box>
  )
}

/** Cópia de um desconto com horários diferentes em cada dia: mostra cada dia como estava e deixa trocar por um horário só. */
const PerDayWindows: React.FC = () => {
  const { d, set } = useWiz()
  const lines = perDayLines(d)
  return (
    <Box id="f-weekdays" role="group" aria-labelledby="h-perday" sx={blockSx}>
      <Hint id="h-perday">{__t('Cada dia tinha um horário. Copiamos como estava.')}</Hint>
      <Box component="ul" sx={perDayListSx}>
        {lines.map(line => (
          <Box component="li" key={line.dow} sx={perDayRowSx}>
            <Box component="span" sx={perDayDaySx}>{line.day}</Box>
            <span>{line.text}</span>
          </Box>
        ))}
      </Box>
      <TextButton onClick={() => set(singleWindow)} sx={{ alignSelf: 'flex-start' }}>{__t('Usar um horário só para todos os dias')}</TextButton>
    </Box>
  )
}

/** Dias da semana (botões) e "O dia todo nesses dias". */
const WeekdayPicker: React.FC = () => {
  const { d, set, fe } = useWiz()
  return (
    <>
      <Box id="f-weekdays" role="group" aria-label={__t('Dias da semana')} aria-describedby={fe['f-weekdays'] ? 'f-weekdays-err' : undefined} sx={weekdayGroupSx}>
        {d.weekdays.map((on, i) => (
          <WeekdayPill key={i} index={i} on={on} toggle={() => set(x => ({ ...x, weekdays: x.weekdays.map((v, j) => (j === i ? !v : v)) }))} />
        ))}
      </Box>
      <CheckRow checked={d.allDay} onChange={() => set(x => ({ ...x, allDay: !x.allDay }))}>{__t('O dia todo nesses dias')}</CheckRow>
    </>
  )
}

/** Começa/termina em, com atalhos de período. */
const PeriodFields: React.FC = () => {
  const { c, d, set, fe } = useWiz()
  const today = todayStr(c.now)
  const describedBy = `d-help${fe['f-dates'] ? ' d-err' : ''}`
  return (
    <Box sx={blockSx}>
      <Box component="span" sx={titleSx}>{__t('Período')}</Box>
      <Box role="group" aria-label={__t('Atalhos de período')} sx={pillsSx}>
        {RANGES.map(id => <Pill key={id} on={d.range === id} onClick={() => set(x => pickPreset(x, id, c.now))}>{presetLabel(id, c.now)}</Pill>)}
      </Box>
      <Box sx={datesSx}>
        <LabeledInput
          id="f-dates" label={__t('Começa em')} type="date" width={180} min={today} value={d.startDate}
          onChange={e => set(x => ({ ...x, startDate: e.target.value, range: null }))} invalid={!!fe['f-dates']} describedBy={describedBy}
        />
        <LabeledInput
          id="d-fim" label={__t('Termina em')} type="date" width={180} min={d.startDate || today} value={d.endDate}
          onChange={e => set(x => ({ ...x, endDate: e.target.value, range: null }))} invalid={!!fe['f-dates']} describedBy={describedBy}
        />
      </Box>
      <Hint id="d-help">{__t('Horário de Brasília.')}</Hint>
      {fe['f-dates'] ? <FieldError id="d-err">{fe['f-dates']}</FieldError> : null}
    </Box>
  )
}

/** Dias e horários dentro do período. */
const HoursFields: React.FC = () => {
  const { d, set, fe } = useWiz()
  const inputs = hasTimeInputs(d)
  const perDay = perDayOn(d)
  const overnight = isOvernight(d)
  return (
    <Box sx={blockSx}>
      <Box id="h-horas" component="span" sx={titleSx}>{__t('Em quais dias e horários?')}</Box>
      <Box role="radiogroup" aria-labelledby="h-horas" sx={hoursGroupSx}>
        <RadioCard name="hours" checked={d.hours === Hours.allday} onChange={() => set(x => ({ ...x, hours: Hours.allday }))} title={__t('O dia todo')} sx={cardTightSx} />
        <RadioCard name="hours" checked={d.hours === Hours.weekdays} onChange={() => set(x => ({ ...x, hours: Hours.weekdays }))} title={__t('Só em alguns dias da semana')} desc={__t('Ex.: toda terça')} sx={cardTightSx} />
        <RadioCard name="hours" checked={d.hours === Hours.daily} onChange={() => set(x => ({ ...x, hours: Hours.daily }))} title={__t('Todo dia, num horário')} desc={__t('Ex.: das 6h às 9h')} sx={cardTightSx} />
      </Box>
      {d.hours === Hours.weekdays ? (perDay ? <PerDayWindows /> : <WeekdayPicker />) : null}
      {inputs ? (
        <Box sx={timesSx}>
          <LabeledInput id="f-times" label={__t('Das')} type="time" width={130} value={d.startTime} onChange={e => set(x => ({ ...x, startTime: e.target.value }))} invalid={!!fe['f-times']} describedBy={fe['f-times'] ? 't-err' : undefined} />
          <LabeledInput id="t-fim" label={__t('Às')} type="time" width={130} value={d.endTime} onChange={e => set(x => ({ ...x, endTime: e.target.value }))} invalid={!!fe['f-times']} describedBy={fe['f-times'] ? 't-err' : undefined} />
          {overnight ? <Hint sx={downPadSx}>{__t('Passa da meia-noite: vale até %{hora} do dia seguinte.', { hora: d.endTime })}</Hint> : null}
        </Box>
      ) : null}
      {fe['f-weekdays'] ? <FieldError id="f-weekdays-err">{fe['f-weekdays']}</FieldError> : null}
      {fe['f-times'] ? <FieldError id="t-err">{fe['f-times']}</FieldError> : null}
    </Box>
  )
}

/** "Quando?": por um período (com atalhos, dias e horários) ou sem data para acabar. */
export const WhenSection: React.FC = () => {
  const { c, d, set, fe, locked, act } = useWiz()
  const period = d.when === When.period
  const sentence = whenSentence(d, c)
  // Todos os clientes e sem data para acabar é um Padrão, a menos que a cidade restrinja o público
  const invalidAlways = d.when === When.always && d.audience === Audience.all && !d.useCity && !locked
  const whenErr = !!fe['f-when'] && !invalidAlways
  const describedBy = whenErr ? 'f-when-err' : invalidAlways ? 'f-when-note' : undefined

  return (
    <Box component="section" aria-labelledby="step-q" sx={stepSx}>
      <RadioRow id="f-when" labelledBy="step-q" describedBy={describedBy}>
        <RadioCard name="when" checked={period} onChange={() => set(x => ({ ...x, when: When.period }))} title={__t('Por um período')} />
        <RadioCard
          name="when"
          checked={!period}
          onChange={() => set(x => ({ ...x, when: When.always }))}
          title={__t('Sem data para acabar')}
        />
      </RadioRow>
      <Box role="status">
        {invalidAlways ? (
          <Callout tone="warn" role="presentation" id="f-when-note" sx={warnTextSx}>
            <Box sx={stackStartSx}>
              <span>{__t('Para todos e sem data para acabar é um Padrão. Escolha um período ou crie como Padrão.')}</span>
              <OutlineButton onClick={act.toDefault} sx={{ height: 40, px: 1.75, fontSize: 14, fontWeight: 700, color: CALLOUT.warnText, borderColor: CALLOUT.warnText }}>
                {__t('Criar como Padrão')}
              </OutlineButton>
            </Box>
          </Callout>
        ) : null}
      </Box>
      {whenErr ? <FieldError id="f-when-err">{fe['f-when']}</FieldError> : null}

      {period ? (
        <Box sx={panelSx}>
          <PeriodFields />
          <HoursFields />
          {sentence ? (
            <Box sx={sentenceBoxSx}>
              <EventIcon aria-hidden sx={eventIconSx} />
              <span>{sentence}</span>
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Box>
  )
}
